import { RecurrenceConverters, RecurrenceType } from '@tableau/self-service-schedules';
import { ButtonWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { FormikSelfServiceSchedulePane } from '~commonInfra/components/FormikSelfServiceSchedulePane';
import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';

import { UpdateScheduleFrequencyServerSettingNames } from '../../../../../backgroundJob/types/UpdateScheduleFrequencyServerSettingRequest';
import { Messages } from '../../../../../codegen/Localize';
import { rejectIfResultsContainError } from '../../../../../httpClient/utils/HttpErrorChecker';
import WebHttpClient from '../../../../../httpClient/WebHttpClient';
import { RunViewRecommendationsTrainerRequest } from '../../../../../recommendations/serverApi/requests/RunViewRecommendationsTrainerRequest';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { ServerSettingsInstance } from '../../../../services/ServerSettings';
import { actionButtonContainer, viewActivityLinkContainer } from '../../../../styles/CommonStyles';
import { useSelector } from '../../../utils';
import { ServerSettingsProps } from '../../server-settings/ReactServerSettings';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';

export const RECOMMENDATIONS_TRAINING_SECTION_TEST_ID = 'recommendations-training-server-setting';
export const RECOMMENDATIONS_TRAINING_LAST_TIMESTAMP_TEST_ID = 'last-schedule-recommendations-trainer-time';
export const RECOMMENDATIONS_TRAINING_VIEW_ACTIVITY_LINK_TEST_ID = 'scheduled-rec-trainer-activity';
export const RECOMMENDATIONS_TRAINING_LAUNCH_BUTTON_TEST_ID = 'run-recommendations-trainer';
export const RECOMMENDATIONS_TRAINING_SCHEDULE_PICKER_LABEL_ID = 'recommendations-training-schedule-picker-label';

export type RecommendationsTrainingSectionProps = ServerSettingsProps<'recommendationsTrainerScheduleFrequency'>;

export function RecommendationsTrainingSection(props: RecommendationsTrainingSectionProps): JSX.Element {
  const isVizRecommendationsEnabled = useSelector(SessionServerSelectors.isVizRecommendationsEnabled);
  const shouldRunViewTraining = FeatureFlags.vizRecommendationsEnabled && isVizRecommendationsEnabled;
  const isRecommendationsEnabled = useSelector(SessionServerSelectors.isRecommendationsEnabled);

  if (!isRecommendationsEnabled) {
    return null;
  }

  return (
    <SettingsSection
      title={Messages.recommendationsTrainerSchedule()}
      description={Messages.recommendationsTrainerScheduleDetails()}
      testId={RECOMMENDATIONS_TRAINING_SECTION_TEST_ID}
    >
      {renderViewActivityLink()}
      {renderLaunchTrainingButton(shouldRunViewTraining)}
      {renderSchedulePicker()}
    </SettingsSection>
  );
}

function renderViewActivityLink(): JSX.Element {
  return <div className={viewActivityLinkContainer}>
    <TextLinkWidget
      href={encodeURI(ServerUrls.getInstance().serverStatusVizUrl('BackgroundTasksforNonExtracts'))}
      target='_blank'
      testId={RECOMMENDATIONS_TRAINING_VIEW_ACTIVITY_LINK_TEST_ID}
    >
      {Messages.recommendationsTrainerScheduleViewUpdateLinkDescription()}
    </TextLinkWidget>
  </div>;
}

function handleApiSuccess(): void {
  ToasterService.instance.pop({
    type: ToastType.Info,
    text: Messages.recommendationsTrainerScheduleRunNowSuccess()
  });
}

function handleApiError(error: any): void {
  let isInProgressError = false;
  if ((Array.isArray(error) && error.some(code => code === CommonErrorCode.InProgress))
      || error === CommonErrorCode.InProgress) {
    isInProgressError = true;
  }

  ToasterService.instance.pop({
    type: ToastType.Error,
    text: isInProgressError ? Messages.recommendationsTrainerSchedulerAlreadyRunning() : Messages.recommendationsTrainerScheduleRunNowError()
  });
}

function renderLaunchTrainingButton(shouldRunViewTraining: boolean): JSX.Element {
  const openConfirmationDialog = () => openReactConfirmationDialog({
    title: Messages.recommendationsTrainerScheduleConfirmTitle(),
    message: Messages.recommendationsTrainerScheduleConfirmBody(),
    actionName: Messages.recommendationTrainerScheduleConfirmButton(),
    action: (shouldRunViewTraining) => {
      if (shouldRunViewTraining) {
        return WebHttpClient.sendRequest(new RunViewRecommendationsTrainerRequest())
          .then(rejectIfResultsContainError)
          .then(handleApiSuccess, handleApiError);
      }
    },
    dialogWidth: TabDialogWidth.width375px,
    paramsForAction: shouldRunViewTraining,
  });

  return <div className={actionButtonContainer}>
    <ButtonWidget
      testId={RECOMMENDATIONS_TRAINING_LAUNCH_BUTTON_TEST_ID}
      onClick={openConfirmationDialog}
    >
      {Messages.recommendationsTrainerScheduleRunNow_action()}
    </ButtonWidget>
  </div>;
}

function renderSchedulePicker(): JSX.Element {
  return (
    <FormikSelfServiceSchedulePane
      name='settings.recommendationsTrainerScheduleFrequency'
      label={Messages.frequency()}
      labelId={RECOMMENDATIONS_TRAINING_SCHEDULE_PICKER_LABEL_ID}
      disableRecurrenceTypes={[RecurrenceType.Hourly]}
    />
  );
}

/* for registering the component in settings registry */
export const RecommendationsTrainingSectionSpecs = componentRegistryEntry({
  name: 'RecommendationsTrainingSection',
  component: RecommendationsTrainingSection,
  onSubmit: (settings) => {
    return ServerSettingsInstance.updateScheduleFrequency(
      UpdateScheduleFrequencyServerSettingNames.recommendationsTrainerScheduleFrequency,
      settings.recommendationsTrainerScheduleFrequency
    );
  },
  transformSettingsOnLoad: (settings) => {
    if (!settings.recommendationsTrainerScheduleFrequency) {
      return settings;
    }
    const patchedScheduleData = RecurrenceConverters.convertRecurrenceToLegacySchedule(
      RecurrenceConverters.castScheduleToRecurrence(settings.recommendationsTrainerScheduleFrequency)
    );
    delete patchedScheduleData.serverTimezoneOffset; // this offset is not used by server settings
    return {...settings, recommendationsTrainerScheduleFrequency: patchedScheduleData};
  }
});
